import type { CSSProperties } from 'react';

import type { PublicTenant } from '@/entities/publicReport';

/**
 * Huisstijl per keuringsbedrijf.
 *
 * De kleuren komen uit de API en worden als custom properties op de
 * rapportpagina gezet. Daardoor werken alle primitives uit het design system
 * ongewijzigd mee: `Button variant="primary"`, `StatusPill tone="accent"` en de
 * energielabelbadge lezen allemaal `var(--accent)`.
 *
 * Drie kleuren komen binnen: het accent, de tekst daarop, en een rustige tweede
 * kleur voor koppen. De API heeft ze al leesbaar gemaakt
 * (`App\Domain\Tenancy\Data\HouseStyle`), dus hier wordt niets meer gecorrigeerd
 * - alleen doorgegeven. Koos het bedrijf geen tweede kleur, dan komt die niet
 * mee en houdt de pagina haar eigen inkt.
 *
 * De statuskleuren van de keuring en de amber van offline staan hier bewust
 * niet tussen: die zeggen wat iets is en zijn bij elk bedrijf gelijk.
 *
 * De afgeleide tinten worden met `color-mix()` berekend in plaats van met een
 * tabel hexcodes - er hoort geen enkele kleurwaarde in deze code te staan.
 */

/** Alleen een kleurwaarde die de browser zelf kan lezen; nooit losse invoer. */
const SAFE_COLOR = /^(#[0-9a-f]{3,8}|rgba?\([\d\s.,%/]+\)|hsla?\([\d\s.,%/deg]+\)|[a-z]{3,20})$/i;

export function isUsableAccent(value: string | null | undefined): value is string {
    return typeof value === 'string' && SAFE_COLOR.test(value.trim());
}

function mix(accent: string, percentage: number, towards: 'white' | 'black'): string {
    return `color-mix(in srgb, ${accent} ${percentage}%, ${towards})`;
}

/**
 * Zet de accentkleur van het bedrijf om in de accenttokens van het design
 * system. Zonder eigen kleur blijft het Smartkeur-groen staan.
 */
export function brandingStyle(
    tenant: Pick<PublicTenant, 'accentColor' | 'accentTextColor' | 'headingColor'> | null,
): CSSProperties {
    const accent = tenant?.accentColor;

    if (!isUsableAccent(accent)) {
        return {};
    }

    const value = accent.trim();
    const onAccent = tenant?.accentTextColor;
    const heading = tenant?.headingColor;

    return {
        ...(isUsableAccent(onAccent) ? { '--accent-text': onAccent.trim() } : {}),
        ...(isUsableAccent(heading) ? { '--heading': heading.trim() } : {}),
        '--accent': value,
        '--accent-hover': mix(value, 86, 'black'),
        '--accent-dark': mix(value, 78, 'black'),
        '--accent-tint': mix(value, 10, 'white'),
        '--accent-tint-2': mix(value, 8, 'white'),
        '--accent-tint-3': mix(value, 5, 'white'),
        '--accent-border': mix(value, 22, 'white'),
        '--accent-tint-hover': mix(value, 16, 'white'),
        '--accent-outline-border': mix(value, 30, 'white'),
        '--accent-outline-hover': mix(value, 12, 'white'),
        '--accent-ink': mix(value, 60, 'black'),
    } as CSSProperties;
}
